'use client';

import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Menu, Button, Group, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import Image from 'next/image';

import UKFlag from '../public/images/flags/en.png';
import UAEFlag from '../public/images/flags/uae.png';
import PakistanFlag from '../public/images/flags/pakistan.png';
import { defaultLocale, locales, type Locale } from '@/lib/i18n/settings';

const LanguageSwitcher = () => {
  const t = useTranslations('languageSwitcher');
  const locale = useLocale() as Locale;
  const router = useRouter();

  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedFlag, setSelectedFlag] = useState(UKFlag);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) {
      const localeMap = {
        en: { label: t('english'), flag: UKFlag },
        ar: { label: t('arabic'), flag: UAEFlag },
        ur: { label: t('urdu'), flag: PakistanFlag },
      } as const;

      const initialLanguage = localeMap[locale]?.label || t('english');
      const initialFlag = localeMap[locale]?.flag || UKFlag;
      setSelectedLanguage(initialLanguage);
      setSelectedFlag(initialFlag);
      document.documentElement.lang = locale;
      document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    } else {
      setMounted(true);
    }
  }, [locale, t, mounted]);

  const changeLanguage = (lng: Locale) => {
    const pathname = window.location.pathname;
    const segments = pathname.split('/').filter(Boolean);
    const hasLocalePrefix = segments.length > 0 && locales.includes(segments[0] as Locale);
    const routeSegments = hasLocalePrefix ? segments.slice(1) : segments;
    const nextPath =
      lng === defaultLocale
        ? `/${routeSegments.join('/')}`.replace(/\/$/, '') || '/'
        : `/${lng}/${routeSegments.join('/')}`.replace(/\/$/, '');

    router.push(nextPath);
  };

  if (!mounted) return null;

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button
          variant="default"
          rightSection={<IconChevronDown size={14} />}
          pr={12}
          styles={(theme) => ({
            root: {
              border: '1px solid rgba(20, 33, 61, 0.12)',
              backgroundColor: 'rgba(255, 255, 255, 0.84)',
              boxShadow: '0 10px 24px rgba(20, 33, 61, 0.08)',
              '&:hover': {
                backgroundColor: theme.white,
              },
            },
            section: {
              marginLeft: theme.spacing.xs,
            },
          })}
        >
          <Group gap="xs">
            <Image src={selectedFlag} width={20} height={20} alt={selectedLanguage} />
            <Text size="sm">{selectedLanguage}</Text>
          </Group>
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          onClick={() => changeLanguage('en')}
          leftSection={<Image src={UKFlag} width={20} height={20} alt={t('english')} />}
        >
          {t('english')}
        </Menu.Item>
        <Menu.Item
          onClick={() => changeLanguage('ar')}
          leftSection={<Image src={UAEFlag} width={20} height={20} alt={t('arabic')} />}
        >
          {t('arabic')}
        </Menu.Item>
        <Menu.Item
          onClick={() => changeLanguage('ur')}
          leftSection={<Image src={PakistanFlag} width={20} height={20} alt={t('urdu')} />}
        >
          {t('urdu')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default LanguageSwitcher;
